import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, PencilSquareIcon, TrashIcon, ChartPieIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { articleService } from '../../api/services';
import { Article, TVA_LABELS, UNITE_LABELS, Page } from '../../types';
import { formatTND } from '../../utils/formatters';
import { DataTable, Column } from '../../components/common/DataTable';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { getCachedArticles } from '../../offlinesync';
import toast from 'react-hot-toast';

export function ArticleList() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tvaFilter, setTvaFilter] = useState<string>('');
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [offline, setOffline] = useState(false);

  const loadArticles = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page: currentPage, size: 10 };
      if (search) params.search = search;
      if (tvaFilter) params.tauxTva = tvaFilter;
      const data: Page<Article> = await articleService.getAll(params);
      setArticles(data.content);
      setTotalElements(data.totalElements);
      setTotalPages(data.totalPages);
      setOffline(false);
    } catch (error) {
      // Offline-first fallback: serve the catalogue cached by OfflineSync instead of an error toast.
      const cached = await getCachedArticles();
      if (cached.length > 0) {
        const term = search.trim().toLowerCase();
        const filtered = term
          ? cached.filter((a) =>
              `${a.code} ${a.designation} ${a.reference ?? ''}`.toLowerCase().includes(term),
            )
          : cached;
        setArticles(filtered);
        setTotalElements(filtered.length);
        setTotalPages(1);
        setOffline(true);
      } else {
        toast.error(t.messages.loadError);
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, search, tvaFilter, t.messages.loadError]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await articleService.delete(deleteId);
      toast.success(t.messages.deleteSuccess);
      loadArticles();
    } catch (error) {
      toast.error(t.messages.deleteError);
    } finally {
      setDeleteId(null);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(0);
    loadArticles();
  };

  const columns: Column<Article>[] = [
    {
      key: 'code',
      header: t.common.code,
      sortable: true,
      render: (article) => (
        <span className="font-medium text-primary-600">{article.code}</span>
      ),
    },
    {
      key: 'designation',
      header: t.common.designation,
      sortable: true,
      render: (article) => (
        <div>
          <div className="font-medium text-gray-900">{article.designation}</div>
          {article.reference && (
            <div className="text-xs text-gray-500">Réf: {article.reference}</div>
          )}
        </div>
      ),
    },
    {
      key: 'prixUnitaireHT',
      header: t.articles.prixUnitaireHT,
      sortable: true,
      render: (article) => (
        <span className="tnd-amount">{formatTND(article.prixUnitaireHT)}</span>
      ),
    },
    {
      key: 'tauxTva',
      header: t.articles.tauxTva,
      render: (article) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
          {TVA_LABELS[article.tauxTva]}
        </span>
      ),
    },
    {
      key: 'unite',
      header: t.articles.unite,
      render: (article) => (
        <span className="text-sm text-gray-600">{UNITE_LABELS[article.unite]}</span>
      ),
    },
    {
      key: 'fodec',
      header: 'FODEC',
      render: (article) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          article.fodec ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-500'
        }`}>
          {article.fodec ? 'Oui' : 'Non'}
        </span>
      ),
    },
    {
      key: 'actif',
      header: t.common.status,
      render: (article) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          article.actif ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
        }`}>
          {article.actif ? t.common.active : t.common.inactive}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t.common.actions,
      render: (article) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/articles/${article.id}/360`);
            }}
            className="text-accent-600 hover:text-accent-800 p-1"
            title="Fiche 360°"
          >
            <ChartPieIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/articles/${article.id}/edit`);
            }}
            className="text-primary-600 hover:text-primary-800 p-1"
            title={t.common.edit}
          >
            <PencilSquareIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteId(article.id);
            }}
            className="text-red-600 hover:text-red-800 p-1"
            title={t.common.delete}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">{t.articles.title}</h1>
          <p className="page-subtitle">{totalElements} articles</p>
        </div>
        <button
          onClick={() => navigate('/articles/new')}
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 mr-1" />
          {t.articles.newArticle}
        </button>
      </div>

      <div className="card">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={`${t.common.search}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10"
            />
          </div>
          <select
            value={tvaFilter}
            onChange={(e) => {
              setTvaFilter(e.target.value);
              setCurrentPage(0);
            }}
            className="input-field sm:w-48"
          >
            <option value="">{t.common.all} - TVA</option>
            {Object.entries(TVA_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <button type="submit" className="btn-primary">
            {t.common.search}
          </button>
        </form>
      </div>

      {offline && (
        <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Mode hors-ligne — données issues du cache local (OfflineSync).
        </div>
      )}

      <DataTable
        columns={columns}
        data={articles}
        totalElements={totalElements}
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        loading={loading}
        emptyMessage={t.common.noResults}
        onRowClick={(article) => navigate(`/articles/${article.id}/edit`)}
      />

      <ConfirmDialog
        isOpen={deleteId !== null}
        title={t.messages.confirmDelete}
        message={t.articles.deleteConfirm}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}

export default ArticleList;
